import { useState } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/adminApi';

export default function Layout() {
  const navigate = useNavigate();
  const { user, companies, unreadNotifications, logout, switchCompany, refreshAccess } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCompanySwitch = async (companyId: string) => {
    if (companyId === user?.companyId) return;
    await switchCompany(companyId);
  };

  const toggleNotifications = async () => {
    const willOpen = !notifOpen;
    setNotifOpen(willOpen);
    if (willOpen) {
      const res = await notificationService.list();
      setNotifications(res.data.notifications || []);
    }
  };

  const markAllRead = async () => {
    await notificationService.markAllRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    await refreshAccess();
  };

  return (
    <div className="h-screen flex bg-gray-50 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-3 flex justify-between items-center">
            <div className="flex items-center gap-3">
              {companies.length > 1 && (
                <select
                  value={user?.companyId || ''}
                  onChange={(e) => handleCompanySwitch(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                  title="Switch company"
                >
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/knowledge-base"
                className="text-gray-600 hover:text-gray-900 text-lg"
                aria-label="Knowledge Base & FAQ"
                title="Knowledge Base & FAQ"
              >
                📖
              </Link>

              <div className="relative">
                <button
                  onClick={toggleNotifications}
                  className="relative text-gray-600 hover:text-gray-900"
                  aria-label="Notifications"
                  title="Notifications"
                >
                  🔔
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border rounded-lg shadow-lg z-10">
                    <div className="flex justify-between items-center px-4 py-2 border-b">
                      <span className="text-sm font-semibold">Notifications</span>
                      <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">
                        Mark all read
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-sm text-gray-500 px-4 py-6 text-center">No notifications</p>
                      ) : (
                        notifications.map((n) => (
                          <div
                            key={n.id}
                            className={`px-4 py-2 text-sm border-b last:border-b-0 ${
                              n.is_read ? 'text-gray-500' : 'text-gray-900 font-medium'
                            }`}
                          >
                            <div>{n.title}</div>
                            {n.message && <div className="text-xs text-gray-500">{n.message}</div>}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <span className="text-sm text-gray-600">{user?.name || user?.email}</span>
              <Link
                to="/change-password"
                className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
                title="Change your password"
              >
                Change Password
              </Link>
              <button
                onClick={handleLogout}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                title="Log out of MAVIS HRMS"
              >
                Logout
              </button>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
